import { eventsService } from './services';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const createAgentActions = (set: any, get: any) => ({
  requireImmediateHelp: async () => {
    const { sdk } = get();
    if (!sdk) return;

    set((state: any) => ({
      urgentHelpInFlight: true,
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          whatToResponse: null,
        },
        lastHelpInteractionTime: Date.now(),
        manualWhatToSayLock: true,
      },
    }));

    try {
      const callId = get().salesCallStatus.callId;
      eventsService.logEvent('URGENT_HELP', {}, callId).catch((e: any) => console.error('[SALES] Failed to log URGENT_HELP event:', e));

      await sdk.sales.requireImmediateHelp();
    } catch (error) {
      set({
        salesError: error instanceof Error ? error.message : 'Failed to request immediate help',
        urgentHelpInFlight: false,
      });
    }
  },

  setTourMode: (active: boolean, businessInfo?: string) => {
    set({
      tourMode: active,
      tourBusinessInfo: active ? businessInfo ?? null : null,
      tourDemoReady: false,
      tourActiveStepId: null,
    });
  },

  setTourActiveStep: (stepId: string | null) => {
    set({ tourActiveStepId: stepId });
  },

  setTourDemoReady: (ready: boolean) => {
    set({ tourDemoReady: ready });
  },

  setDashboardTourActive: (active: boolean) => {
    set({ dashboardTourActive: active });
  },

  setSpotlightTourActive: (active: boolean) => {
    set({ spotlightTourActive: active });
  },

  clearWhatToSayData: () => {
    set((state: any) => ({
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          whatToResponse: null,
          whatToResponseHistory: [],
        },
        manualWhatToSayLock: false,
      },
    }));
  },

  populateTourWhatToSayDemo: (demo?: { what_to_say?: string; how_to_say?: string; triggered_by?: string }) => {
    set((state: any) => {
      const existing = state.salesCallStatus.agentData.whatToResponse;
      const existingHistory = state.salesCallStatus.agentData.whatToResponseHistory || [];
      const hasContent =
        existing && (existing.what_to_say || existing.message || existing.text || existing.how_to_say);
      if (hasContent || existingHistory.length > 0) {
        return {};
      }

      const payload = {
        isStreaming: false,
        what_to_say: demo?.what_to_say ?? 'אני מבין אותך לחלוטין, ספר לי קצת יותר על מה שחשוב לך עכשיו כדי שאוכל להתאים את ההצעה במדויק.',
        how_to_say: demo?.how_to_say ?? 'דבר ברוגע ובאמפתיה',
        message: '',
        text: '',
        triggered_by: demo?.triggered_by ?? 'agent_decision',
        timestamp: new Date().toISOString(),
      };

      return {
        salesCallStatus: {
          ...state.salesCallStatus,
          agentData: {
            ...state.salesCallStatus.agentData,
            whatToResponse: payload,
            whatToResponseHistory: [payload],
          },
        },
      };
    });
  },

  runTourAgent: async (agentName: string, questionText: string) => {
    let { sdk } = get();
    if (!sdk) {
      get().initializeSDK?.();
      sdk = get().sdk;
    }
    if (!sdk) {
      set({ tourDemoReady: true });
      return;
    }

    const timestamp = new Date().toISOString();
    const { tourBusinessInfo } = get();

    set((state: any) => ({
      tourDemoReady: false,
      salesCallStatus: {
        ...state.salesCallStatus,
        lastHelpInteractionTime: Date.now(),
        manualWhatToSayLock: true,
        agentData: {
          ...state.salesCallStatus.agentData,
          whatToResponse: {
            ...(state.salesCallStatus.agentData.whatToResponse || {}),
            isStreaming: true,
            what_to_say: null,
            message: '',
            text: '',
            triggered_by: agentName,
            timestamp,
          },
        },
      },
    }));

    try {
      const result = await sdk.sales.runTourAgent({
        agentName,
        questionText,
        businessInfo: tourBusinessInfo ?? '',
      });

      const fullText = String(result?.result ?? '');
      const chunkSize = fullText.length > 320 ? 18 : 12;
      let streamedText = '';

      for (let index = 0; index < fullText.length; index += chunkSize) {
        streamedText += fullText.slice(index, index + chunkSize);

        set((state: any) => ({
          salesCallStatus: {
            ...state.salesCallStatus,
            lastHelpInteractionTime: Date.now(),
            manualWhatToSayLock: true,
            agentData: {
              ...state.salesCallStatus.agentData,
              whatToResponse: {
                ...(state.salesCallStatus.agentData.whatToResponse || {}),
                isStreaming: true,
                what_to_say: null,
                message: streamedText,
                text: streamedText,
                triggered_by: agentName,
                timestamp,
              },
            },
          },
        }));

        await delay(streamedText.length < fullText.length ? 32 : 0);
      }

      const payload = {
        isStreaming: false,
        what_to_say: null,
        message: fullText,
        text: fullText,
        triggered_by: agentName,
        timestamp: new Date().toISOString(),
      };

      set((state: any) => ({
        tourDemoReady: true,
        salesCallStatus: {
          ...state.salesCallStatus,
          lastHelpInteractionTime: Date.now(),
          manualWhatToSayLock: true,
          agentData: {
            ...state.salesCallStatus.agentData,
            whatToResponse: payload,
            whatToResponseHistory: [
              ...(state.salesCallStatus.agentData.whatToResponseHistory || []),
              payload,
            ],
          },
        },
      }));
    } catch (error) {
      console.error('[TOUR_AGENT] Failed to run tour agent:', error);
      set({
        tourDemoReady: true,
        salesError: error instanceof Error ? error.message : 'Failed to run tour agent',
      });
    }
  },

  askChecklistQuestion: async (questionId: string, questionText: string) => {
    console.log('[PERFORMANCE] Checklist question requested', {
      timestamp: Date.now(),
      id: questionId
    });

    const { sdk } = get();
    if (!sdk) return;

    set((state: any) => ({
      checklistQuestionInFlight: questionId,
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          whatToResponse: null,
        },
        lastHelpInteractionTime: Date.now(),
        manualWhatToSayLock: true,
      },
    }));

    try {
      const callId = get().salesCallStatus.callId;
      eventsService.logEvent('CHECKLIST_QUESTION', { questionId, questionText }, callId)
        .catch((e: any) => console.error('[CHECKLIST] Failed to log CHECKLIST_QUESTION event:', e));

      await sdk.sales.askChecklistQuestion({ questionId, questionText });
    } catch (error) {
      console.error('[CHECKLIST_QUESTION] Failed to ask checklist question:', error);
      set({
        salesError: error instanceof Error ? error.message : 'Failed to ask checklist question',
        checklistQuestionInFlight: null,
      });
    }
  },

  digDeeper: async (questionId: string, questionText: string) => {
    const { sdk } = get();
    if (!sdk) return;

    set((state: any) => ({
      checklistQuestionInFlight: questionId,
      digDeeperInFlight: true,
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          whatToResponse: null,
        },
        lastHelpInteractionTime: Date.now(),
        manualWhatToSayLock: true,
      },
    }));

    try {
      const callId = get().salesCallStatus.callId;
      eventsService.logEvent('DIG_DEEPER', { questionId, questionText }, callId)
        .catch((e: any) => console.error('[DIG_DEEPER] Failed to log DIG_DEEPER event:', e));

      await sdk.sales.digDeeper({ questionId, questionText });
    } catch (error) {
      console.error('[DIG_DEEPER] Failed to dig deeper:', error);
      set({
        salesError: error instanceof Error ? error.message : 'Failed to dig deeper',
        checklistQuestionInFlight: null,
        digDeeperInFlight: false,
      });
    }
  },

  handleObjection: async (objectionName: string, objectionSolution: string, currentSummary?: any) => {
    const { sdk, objectionInFlight } = get();
    if (!sdk) return;
    if (objectionInFlight) return;

    const normalizedObjectionName = (objectionName || '').trim() || 'Objection';
    const objectionTrigger = `Objection: ${normalizedObjectionName}`;

    set((state: any) => ({
      objectionInFlight: true,
      salesCallStatus: {
        ...state.salesCallStatus,
        agentData: {
          ...state.salesCallStatus.agentData,
          whatToResponse: {
            ...(state.salesCallStatus.agentData.whatToResponse || {}),
            message: normalizedObjectionName,
            text: normalizedObjectionName,
            what_to_say: null,
            how_to_say: null,
            triggered_by: objectionTrigger,
            timestamp: new Date().toISOString(),
            isStreaming: true,
          },
        },
        lastHelpInteractionTime: Date.now(),
        manualWhatToSayLock: true,
      },
    }));

    try {
      const callId = get().salesCallStatus.callId;
      eventsService.logEvent('OBJECTION_HANDLING', { objectionName, objectionSolution }, callId)
        .catch((e: any) => console.error('[SALES] Failed to log OBJECTION_HANDLING event:', e));

      if (sdk.sales.handleObjection) {
        await sdk.sales.handleObjection({
          resistence_name: objectionName,
          resistence_solutions: objectionSolution,
          current_summary: currentSummary || {}
        });
      } else {
        console.warn('[SALES] sdk.sales.handleObjection not found, attempting direct socket send or fallback');
      }
    } catch (error) {
      console.error('[SALES] Failed to handle objection:', error);
      set({
        salesError: error instanceof Error ? error.message : 'Failed to handle objection',
        objectionInFlight: false,
      });
    }
  },

  sendFullTranscriptToAgent: async (summaryOverride?: string) => {
    const { sdk } = get();
    if (!sdk) return;
    try {
      const summaryText = summaryOverride !== undefined ? summaryOverride : (get()?.salesCallStatus?.agentData?.summarizer?.summary || '');
      console.log('[SALES] Sending full transcript with summary length:', summaryText.length);

      const callId = get().salesCallStatus.callId;
      eventsService.logEvent('WHATSAPP_CREATION', { summaryLength: summaryText.length }, callId)
        .catch((e: any) => console.error('[SALES] Failed to log WHATSAPP_CREATION (sendFullTranscript) event:', e));

      const res = await sdk.sales.sendFullTranscript(summaryText);

      let resultText = '';
      if (res && typeof res === 'string') {
        resultText = res;
      } else if (res && typeof res === 'object') {
        resultText = (res as any).result || (res as any).message || (res as any).text || (res as any).summary || '';

        if (!resultText && Object.keys(res).length > 0) {
          console.warn('[SALES] Received object response but found no known text field. Response keys:', Object.keys(res));
        }
      }

      set((state: any) => ({
        salesCallStatus: {
          ...state.salesCallStatus,
          agentData: {
            ...state.salesCallStatus.agentData,
            fullTranscriptResult: {
              result: resultText,
              ts: Date.now(),
            },
          },
        },
      }));

    } catch (error) {
      set({
        salesError: error instanceof Error ? error.message : 'Failed to send full transcript',
      });
    }
  },

  generateWhatsAppMessages: async (summary?: string) => {
    const { sdk } = get();
    if (!sdk) return;
    try {
      const callId = get().salesCallStatus.callId;
      eventsService.logEvent('WHATSAPP_CREATION', { summaryLength: summary?.length || 0 }, callId)
        .catch((e: any) => console.error('[SALES] Failed to log WHATSAPP_CREATION (generateWhatsAppMessages) event:', e));

      const res = await sdk.sales.generateWhatsAppMessages(summary);
      let resultText = '';

      if (res && typeof res === 'string') {
        resultText = res;
      } else if (res && typeof res === 'object') {
        resultText = (res as any).result || (res as any).message || (res as any).text || '';
      }

      set((state: any) => ({
        salesCallStatus: {
          ...state.salesCallStatus,
          agentData: {
            ...state.salesCallStatus.agentData,
            whatsappResult: {
              whatsapp_message: resultText,
            },
          },
        },
      }));

      return res;
    } catch (error) {
      console.error('Failed to generate WhatsApp messages:', error);
      throw error;
    }
  },
});
